/**
 * FADING (cee-lo) — non-banker players cover the banker's pot by
 * dropping bills into the bank zone. The banker watches with a closed
 * wallet; faders' wallets open.
 *
 * The banker's instruction line is empty by design — the banner hides
 * for them so they're not staring at static copy while waiting.
 *
 * onUpdate is intentionally narrow: the camera/wallet/instruction
 * setup from onEnter stays valid for the whole phase. Player roster
 * changes (joins/leaves/balance ticks) flow through _updateProfiles
 * + _checkPlayers, but the betting framing stays put.
 *
 * onExit is empty — the next state (PLAYER_BETTING) reuses the
 * betting camera + wallet via its own idempotent enterBettingMode.
 */

import type { GameUpdate } from '@sdl/shared';
import { State } from '../State.js';

export class FadingState extends State {
  async onEnter(update: GameUpdate): Promise<void> {
    const game = this.game;
    const localId = game.getLocalPlayerId();
    const isLocalBanker = !!localId && update.bankerId === localId;

    game.playerProfilesOverlay?.show();
    game.playerProfilesOverlay?.setMode('betting');
    game.bettingHUD3D?.setLockInVisible(false);
    game.countdownOverlay?.stop();

    // Wallet open for the FADERS (everyone except the banker).
    await game.enterBettingMode({
      open: !isLocalBanker,
      animated: update.animated,
      previousState: update.previousState,
    });

    if (isLocalBanker) {
      game.instructionBanner?.hide();
    } else {
      game.instructionBanner?.show(
        {
          title: "Banker's Bet",
          text: 'Fade or part-fade the banker. Drag bills onto the pot',
        },
        update.animated,
      );
    }

    this._updateProfiles(update);
    await this._checkPlayers(update);
  }

  async onUpdate(update: GameUpdate): Promise<void> {
    this._updateProfiles(update);
    await this._checkPlayers(update);
  }

  async onExit(_update: GameUpdate): Promise<void> {
    // Clear the BETTING activity pill — PLAYER_BETTING reuses the
    // betting camera + wallet on its own entry, so nothing else
    // to tear down.
    this._clearBettingActivity();
  }
}
